/**
 * Who is signed in.
 *
 * The access token lives in MEMORY and nowhere else, so a reload loses it —
 * which is the point. What survives a reload is the refresh token, and it is
 * an httpOnly cookie this code cannot read, write or leak. On boot the app
 * asks the API to exchange it; if there is no cookie, the answer is "signed
 * out", which is a state and not an error.
 *
 * That last part matters more than it looks. Most visitors are anonymous and
 * always will be: they open a festival's lineup, save a few acts and never
 * make an account. A failed refresh must therefore be completely silent — no
 * error screen, no toast, no redirect to a sign-in page.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type React from 'react';
import { afterSignOutUrl } from '@/lib/tenant';
import { inNativeApp } from '@/lib/nativeBridge';
import { shownLocaleHint } from '@/lib/shownLocale';
import { QueryClientContext } from '@tanstack/react-query';
import { adoptSession, earlyRefresh, onLateMyFestivals, onSession, refreshInFlight, refreshSession, revokeSession, sessionFamily, sessionStale, setAccessToken, setCurrentAccount, api, type SessionAnswer } from '@/lib/api';
import { lastSignIn, lastSignInHasSecondStep, lastSignOutAt, markSignedOut, revokeSettled } from '@/lib/signOut';
import { noteReentry, SESSION_USER_KEY } from '@/lib/offlineReentry';
import { forgetReturn } from './ReturnAfterSignIn';
import { forgetThisDevice } from '@/offline/push';
import { readJson, remove, writeJson } from '@/lib/storage';
import { isFramed } from '@/lib/framed';
import { clearBounce } from './loginBounce';

export interface AccountUser {
  id: string;
  email: string | null;
  role: string;
  displayName: string | null;
  /**
   * Has an admin let this account in? (`plan.md` §6.12)
   *
   * An unapproved account now HOLDS a session — it signs in, stays signed in
   * across reloads, and can sign out — and the API refuses every route but
   * the few that let it finish or leave. This is what the shell reads to show
   * a waiting screen instead of an app that answers 403 to everything.
   *
   * **It is not a permission.** The API refuses regardless of what the client
   * believes; this only decides what a person is shown.
   */
  approved: boolean;
  /**
   * A provider's own picture, as a URL they host — Google's `picture`,
   * Facebook's. Null for a password account, and null until somebody signs in
   * with a provider that offers one, so every consumer must have a fallback.
   */
  avatarUrl: string | null;
  /**
   * The first name in the languages the person uses (step 98) — kept with the remembered account so the
   * loading lines can greet them by it before anything is asked.
   */
  nameLocal?: Record<string, string>;
}

interface AuthValue {
  user: AccountUser | null;
  /** True until the boot refresh has settled, so nothing flashes "signed out". */
  loading: boolean;
  /**
   * The server could not be heard, so the session is UNVERIFIED — offline,
   * it will reconnect.
   *
   * `user` is then the account last signed in on this origin (remembered
   * without any token), or null if there never was one. Either way nobody is
   * sent to the login host: with no signal it is a dead page, and the lineup
   * this phone already holds is the whole point of the app. The API still
   * refuses whatever it refuses; this only decides what is SHOWN.
   */
  offline: boolean;
  /**
   * Step one of a password sign-in (2026-10-11): a right password signs
   * nobody in — the API emails a code and answers where it went. Resolves
   * with that; `confirmSignInCode` is step two. Asking again sends a new code.
   */
  signIn(email: string, password: string): Promise<SignInCode>;
  /** Step two: the emailed code for the session. */
  confirmSignInCode(challenge: string, code: string): Promise<void>;
  /**
   * Register, and be signed in — approved or not (`plan.md` §6.12).
   *
   * **This used to have two outcomes.** While approval was required the API
   * answered 202 with no tokens, so registering could succeed and leave the
   * person holding nothing, and three screens grew a "you are on the list"
   * branch for it. The session is issued now and `RequireApproval` in the shell
   * is the one place that says somebody is waiting — which is also the only way
   * it survives a reload.
   */
  signUp(email: string, password: string, displayName?: string): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Mint a new access token and re-read the account.
   *
   * Exposed for the waiting screen (`plan.md` §6.12). Approval rides in the
   * ACCESS TOKEN, so an admin letting somebody in does not reach a tab already
   * open until the next token — and without a way to ask, that person waits
   * out `JWT_ACCESS_TTL` wondering whether it worked.
   */
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthValue>({
  user: null,
  loading: true,
  offline: false,
  signIn: async () => ({ challenge: '', sentTo: '' }),
  confirmSignInCode: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  refresh: async () => {},
});

/** Where a password sign-in's code went — `d•••@gmail.com` — and its id. */
export interface SignInCode { challenge: string; sentTo: string }

interface AuthResponse {
  accessToken: string;
  user: AccountUser;
  /** The second step this sign-in owes (`SessionAnswer.mfa`, `RequireMfa`). */
  mfa?: SessionAnswer['mfa'];
}

/**
 * The last account signed in on this origin — never a token.
 *
 * What an offline launch needs to render the rave rather than a login page it
 * cannot reach: who, their role, whether they are approved, their name.
 * Platform-scoped (`myrave:platform:session-user`); the refresh token stays an
 * httpOnly cookie this code cannot read. Cleared on sign-out and whenever the
 * server says nobody is signed in.
 *
 * **And on a sign-out made on ANOTHER host.** Storage is per origin, so a
 * sign-out on `app.` used to leave the person remembered on every rave they
 * had opened — offline, still "signed in". `rememberedAt` is compared with the
 * device-wide sign-out marker (`lib/signOut.ts`); a remembered account older
 * than the last sign-out on this device is forgotten, offline included.
 */
const REMEMBERED = SESSION_USER_KEY;
type RememberedUser = Pick<AccountUser, 'id' | 'role' | 'approved' | 'displayName' | 'nameLocal'> & { rememberedAt?: number };

function remember(user: AccountUser): void {
  const { id, role, approved, displayName, nameLocal } = user;
  writeJson(null, REMEMBERED, { id, role, approved, displayName, nameLocal: nameLocal ?? {}, rememberedAt: Date.now() } satisfies RememberedUser);
  // What lets the same account back into its offline rave after a sign-out with no signal (`lib/offlineReentry.ts`).
  void noteReentry(user, lastSignInHasSecondStep()).catch(() => undefined);
}

/** Somebody signed out on this device after this origin last remembered its account. */
function signedOutSince(r: RememberedUser): boolean {
  const out = lastSignOutAt();
  return out !== null && out >= (r.rememberedAt ?? 0);
}

export function rememberedUser(): AccountUser | null {
  const r = readJson<RememberedUser | null>(null, REMEMBERED, null);
  if (!r || typeof r.id !== 'string' || typeof r.role !== 'string') return null;
  if (signedOutSince(r)) { forget(); return null; }
  return { id: r.id, role: r.role, approved: r.approved === true, displayName: r.displayName ?? null, email: null, avatarUrl: null };
}

/**
 * The same person, saying the same things — so the SAME object is kept.
 *
 * Every refresh (the proactive one each ~14 minutes, the page coming back into
 * view, a 401 retried) hands over a fresh `user`. Adopting it as a new object
 * re-ran everything keyed on `user` and, through `CompleteAccount`, unmounted
 * the whole app to say "One moment…" — an open sheet closed under somebody's
 * thumb four times an hour. A refresh must change nothing on screen unless the
 * account itself changed.
 */
function sameUser(a: AccountUser | null, b: AccountUser | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.role === b.role && a.approved === b.approved
    && a.displayName === b.displayName && a.email === b.email && a.avatarUrl === b.avatarUrl;
}

function forget(): void {
  remove(null, REMEMBERED);
}

/**
 * How long the boot waits to hear whether somebody is signed in: the pilot's
 * 2.5s. On festival wifi that neither answers nor fails, "One moment…" used to
 * be the whole app, forever.
 */
export const BOOT_REFRESH_TIMEOUT_MS = 2500;

/*
 * The boot refresh is `refreshSession` (lib/api): single flight, module scope,
 * so StrictMode's second mount shares the first one's request rather than
 * rotating the cookie twice — the rotation that once left a dozen live
 * sessions on one account after an afternoon of development.
 */

/**
 * Wait for any refresh that is in flight, then carry on.
 *
 * **Call this before LEAVING the page.** Refresh tokens rotate: the server
 * revokes the old one and the browser only learns the new one when the
 * response arrives. A full navigation — which is how this app crosses from a
 * rave to `app.` and back — cancels that response, so the server has moved on
 * while the browser still holds a token that no longer works. The next page
 * gets a 401 and sends the person to a login screen they already passed.
 *
 * That was reported as "going from the rave app to My raves asks me to log in
 * again" on 2026-09-24, and it is a NAVIGATION bug, not an auth one. The
 * server's rule — a rotated token is dead the instant it is rotated — is the
 * thing that stops a stolen token working, and `accounts.test.ts` pins it.
 * Waiting the few hundred milliseconds costs nothing and breaks nothing.
 *
 * Resolves either way: a failed refresh means no session, which the
 * destination will work out for itself.
 */
export async function whenAuthSettled(): Promise<void> {
  try {
    await refreshInFlight();
  } catch {
    // No session, or no network. Both are the destination's problem.
  }
}

/** Leave for another host, without stranding a refresh in flight. */
export async function leaveTo(url: string): Promise<void> {
  // Framed in the setup wizard's preview (round 7, point 49): nothing leaves
  // the rave — the frame stays a picture of it.
  if (isFramed()) return;
  await whenAuthSettled();
  window.location.assign(url);
}

/**
 * The same guard, for a real `<a href>`.
 *
 * Links to a rave stay links — middle-click, ⌘-click and "open in new tab"
 * are worth keeping, and a button cannot do any of them. This intercepts only
 * the plain left-click that would navigate THIS tab, lets every modified
 * click through untouched, and does nothing at all once the refresh has
 * settled (the common case), so the cost is one already-resolved await.
 */
export function guardedNavigation(href: string) {
  return (e: React.MouseEvent) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!refreshInFlight()) return;
    e.preventDefault();
    void leaveTo(href);
  };
}

/** How often a device on its remembered account (no session yet) asks for one. */
export const SESSION_RETRY_MS = 20_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AccountUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  const adopt = useCallback((result: AuthResponse) => {
    // In memory only. Anything in localStorage is readable by any script that
    // ends up on the page — which is why only WHO is remembered, never a token.
    setAccessToken(result.accessToken);
    // Kept like the boot refresh's, so the authenticator gate's first render already knows (`RequireMfa`).
    adoptSession(result);
    // A new session supersedes any sign-out still waiting to reach the server
    // (the API clears the flag too; this covers a host it cannot reach).
    revokeSettled();
    setUser((u) => (sameUser(u, result.user) ? u : result.user));
    remember(result.user);
    setOffline(false);
  }, []);

  /*
   * Every refresh that HEARS the server lands here — the boot one, the one a
   * 401 triggers, the proactive one before expiry, one that timed out at boot
   * and arrived later. A session is adopted; "nobody is signed in" signs out
   * (and `RequireSession` sends the person to sign in, as it always did).
   */
  /*
   * A sign-out under way leaves by its OWN navigation. Meanwhile a request
   * still on the page refreshes and finds nobody; emptying the user then let
   * `RequireSession` go to the sign-in too — two loads (the user, 2026-10-16,
   * closing the account: loading → sign-in → loading → sign-in).
   */
  const leaving = useRef(false);
  const applySession = useCallback((session: SessionAnswer | null) => {
    if (!session && leaving.current) return;
    setOffline(false);
    if (session) {
      setUser((u) => (sameUser(u, session.user) ? u : session.user));
      remember(session.user);
    } else {
      setUser(null);
      forget();
    }
  }, []);
  useEffect(() => onSession(applySession), [applySession]);

  useEffect(() => {
    let cancelled = false;
    // Empty body: the cookie is the credential. `credentials: 'include'` on
    // the client is what actually sends it cross-origin, since the app is on a
    // festival subdomain and the API is on `api.`.
    // The early refresh the app started before this mounted, if any — its
    // `onSession` may have fired before the listener above existed, so its
    // answer is applied here too. Otherwise the boot refresh, as always.
    const started = earlyRefresh(BOOT_REFRESH_TIMEOUT_MS);
    (started ?? refreshSession(BOOT_REFRESH_TIMEOUT_MS))
      .then((session) => { if (!cancelled && started) applySession(session); })
      .catch(() => {
        /*
         * No answer in 2.5s, or no network at all. NOT "signed out": the
         * person who was signed in yesterday is still the person holding the
         * phone. Show the rave as them, from what is stored, and let the
         * refresh land when it can.
         */
        if (cancelled) return;
        const known = rememberedUser();
        if (known) setUser((u) => u ?? known);
        setOffline(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /*
   * Who the sync queue tags a change with (`offline/sync.ts`) — the person on
   * screen, which offline is the remembered account and there is no token.
   */
  const userId = user?.id ?? null;
  useEffect(() => { setCurrentAccount(userId); }, [userId]);

  /*
   * A different person on screen — signed in, signed out, another account — and nothing asked as the
   * one before is kept: every `['me', …]` answer is dropped and asked again (the user, 2026-10-29:
   * signed in on the main app, My raves showed no raves until a reload showed three). The first
   * account of a visit drops nothing — nothing was asked before it.
   */
  const queries = useContext(QueryClientContext);
  const shownAs = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (loading) return;
    const before = shownAs.current;
    shownAs.current = userId;
    if (before === undefined || before === userId || !queries) return;
    void queries.resetQueries({ queryKey: ['me'] });
  }, [userId, loading, queries]);

  // The network's answer to My raves that landed after the phone's copy was shown.
  useEffect(() => {
    if (!queries) return undefined;
    return onLateMyFestivals((answer) => { queries.setQueryData(['me', 'festivals'], answer); });
  }, [queries]);

  /*
   * Get the session back when it can be had: the network returning, and the
   * page coming back into view (a phone asleep through the token's expiry
   * wakes to this, and a timer set before sleep fires late or never).
   */
  /*
   * One session for every tab (the user, 2026-10-13: "two tabs, separate
   * sessions — they need to share the same one"): coming back into view, a tab
   * signed out picks up a sign-in made in another tab or host since this tab
   * last knew (`lastSignIn`, a platform cookie), and a tab showing another
   * account picks up the new one — without waiting for its token to run out.
   * Only on coming into view, so two tabs never rotate the cookie at once.
   */
  /*
   * **The first moment the server answers, a fresh session** (the user,
   * 2026-10-15: "force a new session — but don't make the user re-login").
   * While this device runs on its remembered account with no session (the
   * boot refresh could not be heard), it asks again on a clock — not only on
   * `online`, which a signal that is ON but useless never sends. The refresh
   * cookie is the device's registration: each answer rotates it and renews
   * its 30 days, so a phone that reaches the server once a month stays in.
   */
  useEffect(() => {
    if (!offline) return undefined;
    const timer = window.setInterval(() => { void refreshSession().catch(() => {}); }, SESSION_RETRY_MS);
    return () => window.clearInterval(timer);
  }, [offline]);

  const knownAt = useRef(Date.now());
  useEffect(() => {
    if (user) knownAt.current = Date.now();
  }, [user]);
  useEffect(() => {
    const retry = () => {
      if (!user && !offline) return;
      if (offline || sessionStale(2 * 60_000)) void refreshSession().catch(() => {});
    };
    const visible = () => {
      if (document.visibilityState !== 'visible') return;
      // Signed out on another host while this one sat in the background.
      const r = readJson<RememberedUser | null>(null, REMEMBERED, null);
      if (user && r && signedOutSince(r)) {
        forget();
        setAccessToken(null);
        setUser(null);
        return;
      }
      const signedIn = lastSignIn();
      const outAt = lastSignOutAt() ?? 0;
      // Another account — or THIS one changed elsewhere (a new photo, `noteAccountChanged`, 2026-10-30).
      if (signedIn && signedIn.at > outAt && ((user && signedIn.userId !== user.id) || signedIn.at > knownAt.current)) {
        knownAt.current = Date.now();
        void refreshSession().catch(() => {});
        return;
      }
      retry();
    };
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [user, offline]);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInCode> => {
    const { challenge, sentTo } = await api.post<SignInCode & { codeRequired: true }>('/auth/login', { email, password, ...shownLocaleHint() });
    return { challenge, sentTo };
  }, []);

  const confirmSignInCode = useCallback(async (challenge: string, code: string) => {
    adopt(await api.post<AuthResponse>('/auth/login/code', { challenge, code }));
  }, [adopt]);

  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    adopt(await api.post<AuthResponse>('/auth/register', { email, password, displayName }));
  }, [adopt]);

  const signOut = useCallback(async () => {
    /*
     * Local state is cleared whatever the server says, and before the revoke
     * (only the push unsubscribe below precedes it, bounded). A sign-out
     * that appears to fail because the network is down is worse than useless —
     * the person in front of the phone wanted to be signed out.
     *
     * `markSignedOut` is what every other host on this device hears (they
     * forget their remembered account, offline included) and it leaves the
     * revoke PENDING: the refresh cookie is httpOnly on the API host and only
     * the API can kill it. The revoke gets 2.5s here — sign-out never hangs —
     * and if it does not land, the next exchange on ANY host sends it before
     * refreshing (`lib/api` `exchange`), so a later launch with a signal
     * cannot quietly sign the person back in.
     */
    // This browser's push subscription goes first, while the access token can
    // still say whose it is (`offline/push.ts` `forgetThisDevice`, review A1):
    // bounded, and it never throws, so it cannot hold the sign-out up.
    leaving.current = true;
    await forgetThisDevice();
    markSignedOut(Date.now(), sessionFamily());
    // A page noted before signing in must not be where the NEXT sign-in lands.
    forgetReturn();
    clearBounce();
    setAccessToken(null);
    setCurrentAccount(null);
    forget();
    try {
      await revokeSession();
    } catch {
      // Queued, see above.
    } finally {
      /*
       * Leave — for the page the person was on, when it can be seen signed
       * out (a rave's pages, Upcoming raves, Artists…), else the nearest one
       * that can (the user, 2026-10-29: "not where you were"; `afterSignOutUrl`).
       *
       * Never a page that needs an account: `RequireSession` would send the
       * person straight to the login page — signing out and then being asked
       * to sign in, which reads as the sign-out having failed. And always a
       * fresh load, even of the same address, so nothing of the account's
       * stays on screen.
       *
       * **`setUser(null)` is deliberately NOT called here.** It re-renders the
       * tree, `RequireSession` sees a null user and navigates to the login
       * page, and that redirect races this one — and wins. A full navigation
       * discards this tree anyway, so clearing React state first buys nothing
       * and costs the destination. The in-memory access token IS cleared,
       * because that is module state the navigation does not reach in time.
       *
       * A full navigation, not a router push: the homepage is another HOST.
       * `replace`, so Back does not return to a page the session no longer
       * opens.
       */
      window.location.replace(afterSignOutUrl(inNativeApp()));
    }
  }, []);

  /**
   * Ask for a new token and take whatever the account now says.
   *
   * Failure is swallowed deliberately: this is a "check again" button on a
   * waiting screen, and a network blip must not throw somebody out of the one
   * page that is telling them to be patient.
   */
  const refresh = useCallback(async () => {
    try {
      // The answer is adopted by the `onSession` listener above.
      await refreshSession();
    } catch {
      // Still waiting, or still offline. The screen is unchanged either way.
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user, loading, offline, signIn, confirmSignInCode, signUp, signOut, refresh }),
    [user, loading, offline, signIn, confirmSignInCode, signUp, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}
